/**
 * Utility functions for generating client IDs
 */

import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Generates a client ID based on member type with auto-incrementing number
 * @param {string} memberType - 'Individual' or 'Business'
 * @returns {Promise<string>} Client ID in format MI-000001 or MB-000001
 */
export async function generateClientId(memberType) {
  try {
    const prefix = memberType === 'Individual' ? 'MI' : 'MB';
    
    // Query the completedInvites collection to find the highest number for this type
    const completedRef = collection(db, 'completedInvites');
    const q = query(
      completedRef,
      orderBy('clientId', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    let nextNumber = 1;
    
    if (!snapshot.empty) {
      const lastDoc = snapshot.docs[0];
      const lastClientId = lastDoc.data().clientId;
      
      if (lastClientId && lastClientId.startsWith(prefix + '-')) {
        const lastNumber = parseInt(lastClientId.substring(3), 10);
        nextNumber = lastNumber + 1;
      }
    }
    
    // Format as 6-digit zero-padded number
    const formattedNumber = nextNumber.toString().padStart(6, '0');
    return `${prefix}-${formattedNumber}`;
    
  } catch (error) {
    console.error('Error generating client ID:', error);
    // Fallback to timestamp-based ID if query fails
    const timestamp = Date.now().toString().slice(-6);
    const prefix = memberType === 'Individual' ? 'MI' : 'MB';
    return `${prefix}-${timestamp}`;
  }
}

/**
 * Validates if a string is a valid client ID format
 * @param {string} clientId - The client ID to validate
 * @returns {boolean} True if valid format, false otherwise
 */
export function isValidClientId(clientId) {
  // Check if it matches the pattern MI-000000 or MB-000000
  const pattern = /^(MI|MB)-\d{6}$/;
  return pattern.test(clientId);
}

/**
 * Extracts the member type from a client ID
 * @param {string} clientId - The client ID
 * @returns {string|null} 'Individual' for MI-, 'Business' for MB-, or null if invalid
 */
export function extractMemberType(clientId) {
  if (!isValidClientId(clientId)) {
    return null;
  }
  return clientId.startsWith('MI-') ? 'Individual' : 'Business';
}

/**
 * Extracts the numeric part from a client ID
 * @param {string} clientId - The client ID
 * @returns {number|null} The numeric part or null if invalid
 */
export function extractClientNumber(clientId) {
  if (!isValidClientId(clientId)) {
    return null;
  }
  return parseInt(clientId.substring(3), 10);
}
